import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Stars({ value, size = 14, interactive = false, onChange }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(value);
        return interactive ? (
          <button key={n} type="button" onClick={() => onChange(n)} data-testid={`review-star-${n}`}
            className={`p-0.5 ${filled ? "text-amber-400" : "text-slate-300"} hover:text-amber-400`}>
            <Star size={size + 6} fill="currentColor" />
          </button>
        ) : (
          <Star key={n} size={size} fill={filled ? "currentColor" : "none"}
            className={filled ? "text-amber-400" : "text-slate-300"} />
        );
      })}
    </div>
  );
}

export default function ProductReviews({ slug, rating = { average: 0, count: 0 } }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [elig, setElig] = useState(null); // {can_review, reason, existing?}
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get(`/reviews/product/${slug}`).then(({ data }) => setReviews(data.reviews)).catch(() => {});
    if (user) {
      api.get(`/reviews/eligibility/${slug}`).then(({ data }) => setElig(data)).catch(() => setElig({ can_review: false }));
    } else {
      setElig(null);
    }
  }, [slug, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Please add a title and review.");
      return;
    }
    setPosting(true);
    try {
      const { data } = await api.post("/reviews", {
        product_slug: slug,
        rating: form.rating,
        title: form.title,
        body: form.body,
      });
      setReviews((r) => [data.review, ...r]);
      setElig({ can_review: false, reason: "already_reviewed", existing: data.review });
      setForm({ rating: 5, title: "", body: "" });
      toast.success("Thanks for your review!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post review");
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="mx-auto max-w-4xl px-5 sm:px-8 pb-16" data-testid="reviews-section">
      <div className="border-t border-slate-100 pt-12">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0f2a5c]">Customer Reviews</h2>
          <div className="flex items-center gap-3" data-testid="reviews-summary">
            <Stars value={rating.average || 0} size={16} />
            <span className="text-sm font-semibold text-slate-700">
              {rating.average ? rating.average.toFixed(1) : "0.0"} · {rating.count} review{rating.count === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        {/* Review form / gates */}
        <div className="mt-8 rounded-2xl border border-slate-100 p-6 bg-slate-50/50" data-testid="review-gate">
          {!user ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <p className="text-sm text-slate-600"><b>Have you tried this product?</b> Sign in as a verified customer to leave a review.</p>
              <div className="flex gap-2">
                <Link to="/login" data-testid="reviews-signin-cta" className="px-5 py-2.5 rounded-full bg-[#173a7a] text-white text-sm font-semibold">Sign in</Link>
                <Link to="/signup" className="px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold">Sign up</Link>
              </div>
            </div>
          ) : elig?.can_review ? (
            <form onSubmit={submit} data-testid="review-form">
              <p className="text-xs uppercase tracking-widest font-bold text-emerald-600 flex items-center gap-2 mb-3">
                <CheckCircle2 size={14} /> Verified purchase — you can review
              </p>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Rating</label>
              <Stars value={form.rating} interactive onChange={(n) => setForm({ ...form, rating: n })} />
              <label className="block text-sm font-semibold text-slate-700 mt-4 mb-1">Title</label>
              <input
                data-testid="review-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={120}
                placeholder="Summarize your experience"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]"
              />
              <label className="block text-sm font-semibold text-slate-700 mt-4 mb-1">Your review</label>
              <textarea
                data-testid="review-body-input"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={4}
                maxLength={2000}
                placeholder="Tell others what you liked or didn't"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a] resize-none"
              />
              <button data-testid="review-submit" disabled={posting}
                className="mt-4 px-6 py-3 rounded-full bg-[#173a7a] text-white font-semibold text-sm hover:bg-[#1a4694] disabled:opacity-60">
                {posting ? "Posting…" : "Post Review"}
              </button>
            </form>
          ) : elig?.reason === "already_reviewed" ? (
            <p className="text-sm text-slate-600" data-testid="review-already">You&apos;ve already reviewed this product. Thanks for your feedback!</p>
          ) : elig?.reason === "not_purchased" ? (
            <p className="text-sm text-slate-600" data-testid="review-not-purchased">
              Only verified purchasers can leave a review. <Link to="/shop" className="font-semibold text-[#173a7a] underline">Buy this product</Link> and come back after your order.
            </p>
          ) : (
            <p className="text-sm text-slate-400">Loading…</p>
          )}
        </div>

        {/* Reviews list */}
        <div className="mt-8 space-y-6" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8" data-testid="reviews-empty">No reviews yet. Be the first to share your experience.</p>
          ) : (
            reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border border-slate-100 p-5" data-testid={`review-item-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-sky-100 text-[#173a7a] font-bold flex items-center justify-center text-sm">
                      {(r.user_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-slate-900">{r.user_name}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1.5">
                        {r.verified && (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <CheckCircle2 size={12} /> Verified purchase
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <Stars value={r.rating} size={14} />
                </div>
                <p className="mt-4 font-display font-bold text-slate-900">{r.title}</p>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{r.body}</p>
                <p className="mt-3 text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
